import { createApp } from "vue";

// Registry of currently-mounted PAGE-LEVEL Vue apps.
//
// These apps live inside the <body> that Turbo replaces on every navigation.
// mountVue used to mount them on each `turbo:load` but nothing ever called
// `app.unmount()`, so their `onUnmounted` hooks never ran: document-level click
// listeners (Friends / GroupList / GroupMembers), Survey's ResizeObserver, and
// the whole detached component tree behind them leaked on every visit. We track
// each app here and unmount them all on `turbo:before-render` (see application.js).
//
// The nav rail and mobile tab bar are intentionally NOT tracked: they are
// `data-turbo-permanent`, so Turbo keeps those exact elements (and their live
// Vue apps) across navigations. Unmounting them would tear down state Turbo is
// deliberately preserving, so they are mounted directly in application.js.
const pageApps = new Set();

export function trackPageApp(app) {
  pageApps.add(app);
  return app;
}

// Unmount every tracked page-level app (running their onUnmounted cleanup) and
// clear the registry. Vue's unmount deletes `__vue_app__` from each container,
// so a later mount attempt on that element (e.g. a Turbo restore visit, whose
// restored DOM has lost the property anyway) mounts cleanly again.
export function unmountPageApps() {
  pageApps.forEach((app) => {
    try {
      app.unmount();
    } catch (error) {
      console.error("Error unmounting Vue app:", error);
    }
  });
  pageApps.clear();
}

// Exposed for tests / diagnostics.
export function mountedPageAppCount() {
  return pageApps.size;
}

// Lazily import a component and mount it into `el`, tracking it for teardown.
// Guards against double-mounting via both the settled (`__vue_app__`) and the
// in-flight (`__vue_mounting__`) states, and bails if the element was detached
// (e.g. a Turbo navigation swapped the body) while the import was in flight.
export async function mountPageApp(el, loader, props = {}) {
  if (!el || el.__vue_app__ || el.__vue_mounting__) {
    return;
  }

  el.__vue_mounting__ = true;

  try {
    const component = (await loader()).default;

    if (!el.isConnected || el.__vue_app__) {
      return;
    }

    const app = createApp(component, props);
    app.mount(el);
    trackPageApp(app);
  } catch (error) {
    console.error("Error mounting Vue component:", error);
  } finally {
    delete el.__vue_mounting__;
  }
}
